import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  applyPrefsToDocument,
  defaultPrefs,
  loadPrefs,
  savePrefs,
  type Preferences,
} from "@/lib/preferences";
import { getSubscriptionByEmail, type SubscriptionStatus } from "@/lib/subscription";
import { useWakeLock } from "@/hooks/useWakeLock";
import {
  clearRememberedEmail,
  getRememberedEmail,
  setRememberedEmail,
} from "@/lib/rememberedEmail";

type PreferencesContextValue = {
  prefs: Preferences;
  setPref: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  reset: () => void;
  signedInEmail: string | null;
  subscriptionActive: boolean;
  subscriptionChecked: boolean;
};

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

const RECIPE_PATH_RE = /^\/(r\/|recipe)/;

export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [prefs, setPrefs] = useState<Preferences>(() => loadPrefs());
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const location = useLocation();

  // Track auth.
  useEffect(() => {
    // Track the last cached value we wrote so TOKEN_REFRESHED churn (which
    // fires periodically with the same email) doesn't repeatedly write to
    // localStorage and emit `storage` events to other tabs.
    let lastWritten: string | null = getRememberedEmail();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      const sessionEmail = session?.user?.email ?? null;
      setSignedInEmail(sessionEmail);
      // On explicit sign-out, clear the cached email so the next user on a
      // shared device starts from a clean slate.
      if (event === "SIGNED_OUT") {
        clearRememberedEmail();
        lastWritten = null;
        return;
      }
      // For every other auth event that yields a session (SIGNED_IN,
      // INITIAL_SESSION, TOKEN_REFRESHED, USER_UPDATED), keep the cached
      // `cp_email` in lockstep with the active account — but ONLY when the
      // value has actually changed. Without the equality check, every
      // periodic TOKEN_REFRESHED event writes to localStorage and broadcasts
      // a `storage` event to other tabs.
      if (sessionEmail && sessionEmail !== lastWritten) {
        setRememberedEmail(sessionEmail);
        lastWritten = sessionEmail;
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      const sessionEmail = session?.user?.email ?? null;
      setSignedInEmail(sessionEmail);
      if (sessionEmail && sessionEmail !== lastWritten) {
        setRememberedEmail(sessionEmail);
        lastWritten = sessionEmail;
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Load subscription whenever signed-in email changes.
  useEffect(() => {
    if (!signedInEmail) {
      setSubscription(null);
      setSubscriptionChecked(true);
      return;
    }
    setSubscriptionChecked(false);
    getSubscriptionByEmail(signedInEmail)
      .then((s) => {
        setSubscription(s);
      })
      .catch((error) => {
        console.error("[usePreferences] subscription lookup failed", error);
        setSubscription(null);
      })
      .finally(() => {
        setSubscriptionChecked(true);
      });
  }, [signedInEmail]);

  const subscriptionActive = !!subscription?.active;

  // Effective prefs: gate to defaults if the user isn't a paid subscriber.
  // This guarantees the app looks identical for free / signed-out users.
  // Exceptions: `units` & `diet` are available to all tiers — they're
  // accessibility / personalisation concerns, not power-user features.
  const effectivePrefs = useMemo<Preferences>(
    () =>
      subscriptionActive
        ? prefs
        : { ...defaultPrefs, units: prefs.units, diet: prefs.diet },
    [prefs, subscriptionActive],
  );

  // Apply CSS vars / dark class / data-text-size whenever effective prefs change.
  useEffect(() => {
    applyPrefsToDocument(effectivePrefs);
  }, [effectivePrefs]);

  // Re-apply on system theme change when "system" is selected.
  useEffect(() => {
    if (effectivePrefs.contrast !== "system") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyPrefsToDocument(effectivePrefs);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [effectivePrefs]);

  // Wake lock — only request while the user is on a recipe page.
  const onRecipePage = RECIPE_PATH_RE.test(location.pathname);
  useWakeLock(effectivePrefs.cookingMode && onRecipePage);

  const setPref = useCallback<PreferencesContextValue["setPref"]>((key, value) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      savePrefs(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setPrefs(defaultPrefs);
    savePrefs(defaultPrefs);
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      prefs: effectivePrefs,
      setPref,
      reset,
      signedInEmail,
      subscriptionActive,
      subscriptionChecked,
    }),
    [effectivePrefs, setPref, reset, signedInEmail, subscriptionActive, subscriptionChecked],
  );

  return (
    <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
  );
};

export function usePreferences(): PreferencesContextValue {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return ctx;
}
